/**
 * Per-row quick actions for the notification table: patient notes and
 * read/unread toggle.
 */

import { Mail, MailOpen, NotebookPen } from "lucide-react";
import type { Patient } from "../../../../types";

interface ActionItemsCellProps {
  patient: Patient;
  /** Open the patient summary with the notes panel docked. */
  onOpenNotes?: () => void;
  /** Return an already-read row to unread (in place, no navigation). */
  onMarkUnread?: () => void;
  /** Mark an unread row as read (in place, no navigation). */
  onMarkRead?: () => void;
  /**
   * Whether the patient has an open note. Set from the facility notes inbox
   * because the worklist row itself does not carry note state.
   */
  hasOpenNote?: boolean;
}

/**
 * Render the "Action Items" cell: a notes shortcut and a read/unread toggle.
 *
 * The notes icon is muted (grey) when the patient has no notes and blue once
 * notes exist — either notes attached to the row or a facility open note flagged
 * via `hasOpenNote`. The mail icon follows the inverse, attention-first convention:
 * a blue closed envelope while the row is unread (needs attention), fading to a
 * grey open envelope once read. Clicks never bubble to the row's open-patient
 * navigation.
 *
 * @param props - {@link ActionItemsCellProps}
 * @returns The composed action-items cell.
 */
export function ActionItemsCell({
  patient,
  onOpenNotes,
  onMarkUnread,
  onMarkRead,
  hasOpenNote,
}: ActionItemsCellProps) {
  const hasNotes = (patient.notes?.length ?? 0) > 0 || hasOpenNote === true;
  const isRead = patient.isRead === true;

  /**
   * Open the patient's notes without triggering row navigation.
   *
   * @param event - Click event from the notes control.
   * @returns Nothing.
   */
  const handleNotesClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onOpenNotes?.();
  };

  /**
   * Toggle the row's read state in place without triggering row navigation.
   *
   * @param event - Click event from the mail control.
   * @returns Nothing.
   */
  const handleReadClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (isRead) {
      onMarkUnread?.();
    } else {
      onMarkRead?.();
    }
  };

  return (
    <div
      className="action-items-cell"
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className={`action-items-cell__btn${hasNotes ? " action-items-cell__btn--active" : ""}`}
        title={hasNotes ? "View patient notes" : "No notes yet — open notes"}
        aria-label={`${hasNotes ? "View" : "Add"} notes for ${patient.name}`}
        onClick={handleNotesClick}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <NotebookPen size={15} aria-hidden="true" />
      </button>
      <button
        type="button"
        className={`action-items-cell__btn${isRead ? "" : " action-items-cell__btn--active"}`}
        title={isRead ? "Mark as unread" : "Mark as read"}
        aria-label={`Mark ${patient.name} as ${isRead ? "unread" : "read"}`}
        onClick={handleReadClick}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {isRead ? (
          <MailOpen size={15} aria-hidden="true" />
        ) : (
          <Mail size={15} aria-hidden="true" />
        )}
      </button>
    </div>
  );
}

export default ActionItemsCell;
